import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { platformAPI, authAPI } from '../utils/api';
import './SuperAdminDashboard.css';

const HEAD_EMAIL = 'sharnadeep25@gmail.com';

function formatMoney(v) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '₹0';
  return `₹${n.toFixed(2)}`;
}

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState({ restaurants: [], summary: null, filter: null });

  const [restaurantId, setRestaurantId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // Frontend guard (backend is the real guard)
  useEffect(() => {
    const email = String(authAPI.getAuthEmail?.() || '').toLowerCase();
    if (email && email !== HEAD_EMAIL) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await platformAPI.getRestaurantsOverview({ restaurantId, from, to });
      setData(res?.data || { restaurants: [], summary: null, filter: null });
    } catch (e) {
      setError(e.message || 'Failed to load platform data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // load() reads current filter state; initial load only.
    // Subsequent loads happen via the Apply/Refresh buttons.
    // eslint rule for exhaustive deps is not enabled in this project.
    // Intentionally run once.
    //
    // eslint-disable-next-line
  }, []);

  const restaurantOptions = useMemo(() => {
    const items = data?.restaurants || [];
    const unique = new Map();
    for (const r of items) {
      if (r?.id) unique.set(r.id, r);
    }
    return Array.from(unique.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [data]);

  return (
    <div className="sad-shell">
      <div className="sad-page">
      <div className="sad-header">
        <h1>Super Admin Dashboard</h1>
        <div className="sad-actions">
          <Link to="/dashboard" style={{ textDecoration: 'none' }}>
            <button type="button">Back</button>
          </Link>
          <button className="primary" type="button" onClick={load} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      <div className="sad-filters">
        <div className="sad-filter">
          <label>Restaurant</label>
          <select value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)}>
            <option value="">All</option>
            {restaurantOptions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>

        <div className="sad-filter">
          <label>From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>

        <div className="sad-filter">
          <label>To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>

        <div className="sad-filter">
          <label>&nbsp;</label>
          <button type="button" onClick={load} disabled={loading}>
            Apply
          </button>
        </div>
      </div>

      {error ? <div className="sad-error">{error}</div> : null}

      {data?.summary ? (
        <div className="sad-summary">
          <div><strong>Restaurants:</strong> {data.summary.restaurants}</div>
          <div><strong>Active restaurants:</strong> {data.summary.activeRestaurants}</div>
          <div><strong>Total orders:</strong> {data.summary.totalOrders}</div>
          <div><strong>Total revenue:</strong> {formatMoney(data.summary.totalRevenue)}</div>
        </div>
      ) : null}

      {loading ? (
        <div className="sad-empty">Loading…</div>
      ) : (data?.restaurants || []).length === 0 ? (
        <div className="sad-empty">No restaurants found for the selected filters.</div>
      ) : (
        <div className="sad-grid">
          {(data.restaurants || []).map((r) => (
            <div key={r.id} className="sad-card">
              <div className="sad-card-title">
                <div className="sad-name">{r.name}</div>
                <div className={r.active ? 'sad-badge active' : 'sad-badge inactive'}>
                  {r.active ? 'Active' : 'Inactive'}
                </div>
              </div>

              <div className="sad-row"><strong>Location:</strong> {r.location || '—'}</div>
              <div className="sad-row"><strong>Admin email:</strong> {r.adminEmail || '—'}</div>
              <div className="sad-row"><strong>Restaurant email:</strong> {r.restaurantEmail || '—'}</div>
              <div className="sad-row"><strong>Menu items:</strong> {Number.isFinite(r.menuItemsCount) ? r.menuItemsCount : 0}</div>
              <div className="sad-row"><strong>Orders:</strong> {r.ordersCount}</div>
              <div className="sad-row"><strong>Revenue:</strong> {formatMoney(r.revenue)}</div>
              <div className="sad-row"><strong>Wi‑Fi only:</strong> {r.wifiOnlyEnabled ? 'Yes' : 'No'}</div>
            </div>
          ))}
        </div>
      )}

      <div className="sad-footnote">
        This page is restricted to the Head of Team ({HEAD_EMAIL}).
      </div>
      </div>
    </div>
  );
}
